import styled, { DefaultTheme, css } from 'styled-components';

type TextProps = {
  color?: keyof DefaultTheme['colors'];
};

export const Wrapper = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.globalWhite100};
    padding: 16px;
    border: 1px solid ${theme.colors.neutralSilver20};
    border-radius: 8px;
  `}
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

type PositioningProps = {
  displayRow?: boolean;
};

export const MonthlyPaymentBox = styled.div<PositioningProps>`
  ${({ displayRow }) => css`
    display: flex;
    flex-direction: column;
    max-width: 70%;
    gap: 16px;
    padding: ${displayRow ? '4px' : '16px'} 0 24px 0;
    order: ${displayRow ? 2 : 1};

    @media (max-width: 576px) {
      gap: 4px;
      order: 1;
    }
  `}
`;

export const TagContainer = styled.div<PositioningProps>`
  ${({ displayRow }) => css`
    display: flex;
    order: ${displayRow ? 1 : 2};
    padding-top: ${displayRow ? '16px' : 0};

    @media (max-width: 576px) {
      order: 2;
      padding-bottom: ${displayRow ? '24px' : 0};
    }
  `}
`;

export const ChosenValue = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const MonthlyPayment = styled.p`
  ${({ theme }) => css`
    font-size: 2.4rem;
    line-height: 2.9rem;
    font-weight: 700;
    color: ${theme.colors.neutralGray100};

    &::after {
      content: '/mo';
      vertical-align: bottom;
      font-size: 2rem;
      line-height: 2.8rem;
      font-weight: 400;
    }
  `}
`;

export const WeeklyPayment = styled.p`
  ${({ theme }) => css`
    font-size: 2.4rem;
    line-height: 2.9rem;
    font-weight: 700;
    color: ${theme.colors.neutralGray100};

    &::after {
      content: '/wk';
      vertical-align: bottom;
      font-size: 2rem;
      line-height: 2.8rem;
      font-weight: 400;
    }
  `}
`;

export const OriginalValueBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const OriginalValue = styled.p`
  ${({ theme }) => css`
    font-size: 1.4rem;
    font-weight: 500;
    line-height: 26px;
    color: ${theme.colors.neutralSilver70};

    &::after {
      content: '/mo';
      vertical-align: bottom;
      font-size: 1.4rem;
      line-height: 2.5rem;
      font-weight: 400;
    }
  `}
`;

export const Label = styled.p<TextProps>`
  ${({ theme, color = 'neutralSilver60' }) => css`
    font-size: 1.2rem;
    line-height: 1.9rem;
    font-weight: 400;
    color: ${theme.colors[color]};

    > span {
      font-weight: 500;
    }

    > strong {
      font-weight: 700;
    }
  `}
`;

type RowProps = {
  border?: boolean;
  displayRow?: boolean;
};

const rowModifiers = {
  row: css`
    flex-direction: column;
    align-items: flex-start;

    @media (max-width: 576px) {
      flex-direction: row;
    }
  `,
};

export const Row = styled.div<RowProps>`
  ${({ theme, border, displayRow }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    border-bottom: ${border
      ? `1px solid ${theme.colors.neutralSilver30}`
      : 'none'};

    ${displayRow && rowModifiers.row}

    @media (max-width: 576px) {
      align-items: center;
    }
  `}
`;

/* Three stats side by side, small-caps label above the value, instead of three
   label-left / value-right rows. Only applies when the consumer asks for
   statsLayout="grid"; nothing else is affected. */
export const StatsGrid = styled.div`
  ${({ theme }) => css`
    display: grid;
    /* auto-fit rather than 3 fixed columns: three fit in the offer's wide card,
       and it wraps to two on its own in a narrow aside, without the labels
       breaking across lines. */
    grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
    gap: 16px;

    /* Cada indicador deixa de ser linha e passa a empilhar. */
    > div {
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      gap: 2px;
      min-width: 0;
    }

    /* A entrada vem embrulhada num wrapper a mais que os outros dois. */
    > div > div {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
      width: 100%;
    }

    /* Label */
    p:first-child {
      font-size: 1.2rem;
      line-height: 1.6rem;
      font-weight: 500;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: ${theme.colors.neutralSilver60};
    }

    /* Valor */
    p:last-child {
      font-size: 1.6rem;
      line-height: 2.4rem;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
      color: ${theme.colors.neutralSilver90};
    }

    @media (max-width: 420px) {
      grid-template-columns: repeat(2, 1fr);
    }
  `}
`;

export const Text = styled.p<TextProps>`
  ${({ theme, color = 'neutralSilver60' }) => css`
    font-size: 1.4rem;
    line-height: 2.2rem;
    font-weight: 400;
    color: ${theme.colors[color]};

    strong {
      font-weight: 500;
      color: ${theme.colors.neutralSilver70};
    }

    span {
      font-size: 1.4rem;
      font-weight: 700;
      color: ${theme.colors.statusSuccess30};
    }

    &:nth-child(even) {
      text-align: right;
    }
  `}
`;

export const Tooltip = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
`;

export const SkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .ant-skeleton-element .ant-skeleton-input {
    height: 18px;
    border-radius: 4px;
  }
`;

export const RowBorderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-bottom: 14px;
`;

export const MonthsRange = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: 20px;
`;
